import React, { useEffect, useRef, useState } from 'react';
import SwiperCore from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/swiper.scss';
import { LinkWithQuery } from 'components/common/LinkWithQuery';
import { homepageSolutions } from 'components/pages/AudienceSolutions/data';
import styles from './styles.editorial.module.scss';

const Solutions = () => {
  const swiperRef = useRef<SwiperCore | null>(null);
  const sectionRef = useRef<HTMLElement | null>(null);
  const [position, setPosition] = useState({ first: 1, last: 1, beginning: true, end: false });
  const [reducedMotion, setReducedMotion] = useState(false);
  const syncPosition = (swiper: SwiperCore) => setPosition({
    first: Math.min(swiper.activeIndex + 1, homepageSolutions.length),
    last: Math.min(swiper.activeIndex + Math.ceil(Number(swiper.params.slidesPerView) || 1), homepageSolutions.length),
    beginning: swiper.isBeginning,
    end: swiper.isEnd,
  });

  useEffect(() => {
    const preference = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setReducedMotion(preference.matches);
    update();
    preference.addEventListener('change', update);
    return () => preference.removeEventListener('change', update);
  }, []);

  useEffect(() => {
    if (window.location.hash !== '#industry-solutions-title') return;
    // Run after route-level scroll restoration, including a direct page load.
    const frame = requestAnimationFrame(() => sectionRef.current?.scrollIntoView({ block: 'start' }));
    return () => cancelAnimationFrame(frame);
  }, []);

  return (
    <section
      ref={sectionRef}
      className={`${styles.solutions} flex flex-col w-full items-center`}
      aria-labelledby="industry-solutions-title"
    >
      <div className={styles.content}>
        <div className={styles.header}>
          <h2 className={styles.title} id="industry-solutions-title">
            Built for the journeys your customers already need.
          </h2>
          <p>
            See what Storycards can help your organization build, from product discovery and guided decisions to
            onboarding, education and qualified handoffs.
          </p>
        </div>
        <div className={styles.sliderWrap} role="region" aria-label="Organization and industry solutions">
          <Swiper
            className={`${styles.slider} ${position.end ? styles.sliderAtEnd : ''}`}
            spaceBetween={16}
            slidesPerView={1.08}
            speed={reducedMotion ? 0 : 400}
            grabCursor
            watchOverflow
            breakpoints={{
              640: { slidesPerView: 1.45 },
              900: { slidesPerView: 2.05 },
              1180: { slidesPerView: 2.55 },
            }}
            onSwiper={(swiper) => {
              swiperRef.current = swiper;
              syncPosition(swiper);
            }}
            onSnapIndexChange={syncPosition}
            onResize={syncPosition}
            onReachEnd={syncPosition}
            onFromEdge={syncPosition}
          >
            {homepageSolutions.map((solution, index) => (
              <SwiperSlide className={styles.slide} key={solution.slug}
                onFocusCapture={() => swiperRef.current?.slideTo(index, 0)}>
                <LinkWithQuery
                  to={solution.route}
                  className={styles.contentItem}
                  style={
                    {
                      '--card-accent': solution.accent,
                      '--card-soft': solution.softAccent,
                    } as React.CSSProperties
                  }
                >
                  <div className={styles.cardHeader}>
                    <span>{String(index + 1).padStart(2, '0')}</span>
                    <i aria-hidden="true">↗</i>
                  </div>
                  <div className={styles.cardCopy}>
                    <h3>{solution.cardTitle}</h3>
                    <p>{solution.cardText}</p>
                  </div>
                  <div className={styles.productList} aria-label={`Examples for ${solution.name}`}>
                    {solution.cardProducts.map((product) => (
                      <span key={product}>{product}</span>
                    ))}
                  </div>
                  <div className={styles.itemCta}>
                    <span>{solution.cardCta || `Explore ${solution.name.toLowerCase()} solutions`}</span>
                    <i aria-hidden="true">→</i>
                  </div>
                </LinkWithQuery>
              </SwiperSlide>
            ))}
          </Swiper>
          <div className={styles.sliderFooter}>
            <span aria-live="polite">
              Showing {position.first} to {position.last} of {homepageSolutions.length}
            </span>
            <div className={styles.controls}>
              <button type="button" disabled={position.beginning} aria-label="Previous solution" onClick={() => swiperRef.current?.slidePrev()}>
                <span aria-hidden="true">←</span>
              </button>
              <button type="button" disabled={position.end} aria-label="Next solution" onClick={() => swiperRef.current?.slideNext()}>
                <span aria-hidden="true">→</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Solutions;
